import { X } from 'lucide-react';
import type { CreateTicketDto } from '@services/ticket.service';
import { TicketForm, type TicketFormValues } from './TicketForm';

interface NewTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (dto: CreateTicketDto) => Promise<void>;
  isLoading?: boolean;
  initialData?: {
    customerId?: string;
    contactEmail?: string;
    contactName?: string;
  };
}

/**
 * Modal chrome around the shared {@link TicketForm}. The standalone create page
 * (/tickets/new) uses the same form; this wrapper exists for inline contexts
 * that still want a popup (e.g. creating a ticket from a customer profile).
 *
 * The form's state is initialised from `initialData` on mount. Because the
 * modal renders nothing while closed, the form unmounts on close and remounts
 * fresh on reopen — no manual reset needed.
 */
export function NewTicketModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  initialData,
}: NewTicketModalProps) {
  if (!isOpen) return null;

  const handleSubmit = async (values: TicketFormValues) => {
    const dto: CreateTicketDto = {
      subject: values.subject,
      description: values.description,
      channel: values.channel,
      priority: values.priority,
      contactEmail: values.contactEmail,
      contactName: values.contactName,
      contactPhone: values.contactPhone,
      customerId: values.customerId,
      tags: values.tags,
      ticketTypeId: values.ticketTypeId,
      // Omit when unset so the backend's auto-assignment runs (an empty string
      // would fail the API's @IsUUID and 400).
      assigneeId: values.assigneeId || undefined,
      customFields: values.customFields,
    };
    await onSubmit(dto);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative w-full max-w-2xl rounded-lg bg-white dark:bg-gray-900 shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Create New Ticket
            </h2>
            <button
              onClick={onClose}
              className="rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Form */}
          <div className="p-6 overflow-y-auto flex-1">
            <TicketForm
              mode="create"
              initialValues={{
                contactEmail: initialData?.contactEmail,
                contactName: initialData?.contactName,
                customerId: initialData?.customerId,
              }}
              onSubmit={handleSubmit}
              onCancel={onClose}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
